import { toast } from "sonner";
import { moduleForbiddenMessage } from "@/lib/modules";
import type { components } from "@dolfin/api-public";

type ProblemDetails = components["schemas"]["ProblemDetails"];

function asProblem(error: unknown): ProblemDetails | undefined {
  return error && typeof error === "object" ? (error as ProblemDetails) : undefined;
}

/** Error code the backend returns when the caller's org role is below the required tier. */
export const INSUFFICIENT_ROLE_TYPE = "Organisation.InsufficientRole";

/** Error code the backend returns when the client's plan does not include the module an endpoint requires. */
export const MODULE_NOT_ENTITLED_TYPE = "Module.NotEntitled";

/** Fired on `window` for each Module.NotEntitled 403: the cached module list is stale. An event keeps this file app-agnostic. */
export const MODULE_FORBIDDEN_EVENT = "dolfin:module-forbidden";

const INSUFFICIENT_ROLE_FALLBACK =
  "You don't have permission to do that. Ask an owner or admin.";

/** The role-gate 403: lets call sites show a friendly message when an optimistic UI gate is stale. */
export function isInsufficientRoleError(error: unknown): boolean {
  return apiErrorType(error).includes(INSUFFICIENT_ROLE_TYPE);
}

/** The typed schema is plain ProblemDetails, so ASP.NET's 400 `errors` dict is read structurally. */
function validationErrorMessages(error: unknown): string[] {
  const errors = (error as { errors?: Record<string, string[]> } | undefined)?.errors;
  if (!errors || typeof errors !== "object" || Array.isArray(errors)) return [];
  return Object.values(errors).flat().filter((m): m is string => Boolean(m));
}

export function apiErrorMessage(error: unknown, fallback: string): string {
  const p = asProblem(error);
  if (isInsufficientRoleError(error)) return p?.detail || INSUFFICIENT_ROLE_FALLBACK;
  const fieldErrors = validationErrorMessages(error);
  if (fieldErrors.length > 0) return fieldErrors.join(" ");
  return p?.detail || p?.title || fallback;
}

/** The backend sends its code as `code` and `title`; `type` is ASP.NET's default RFC link, so only a last resort. */
export function apiErrorType(error: unknown): string {
  const p = asProblem(error) as (ProblemDetails & { code?: string | null }) | undefined;
  return p?.code ?? p?.title ?? p?.type ?? "";
}

/** 403 Module.NotEntitled: about what the client bought, unlike `isInsufficientRoleError` (the person). */
export function isModuleNotEntitledError(error: unknown): boolean {
  return apiErrorType(error).includes(MODULE_NOT_ENTITLED_TYPE);
}

/** The backend quotes the module only in `detail` ("…requires the 'ap' module…"). */
export function moduleFromError(error: unknown): string | null {
  const detail = asProblem(error)?.detail;
  return detail?.match(/'([a-z][a-z0-9.]*)'/i)?.[1] ?? null;
}

/**
 * Deduped by module key, so a page firing several gated requests gets one toast. Role 403s are toasted by the
 * call site instead (see `toastApiError` in CLAUDE.md).
 */
export function notifyForbidden(error: unknown): void {
  if (!isModuleNotEntitledError(error)) return;
  const module = moduleFromError(error);
  toast.error(moduleForbiddenMessage(module), { id: `module-forbidden:${module ?? "unknown"}` });
  window.dispatchEvent(new CustomEvent(MODULE_FORBIDDEN_EVENT, { detail: { module } }));
}

/** Reads the body from a clone, so it is safe in client middleware. */
export async function notifyForbiddenFromResponse(response: Response): Promise<void> {
  if (response.status !== 403) return;
  const problem = await response.clone().json().catch(() => null);
  notifyForbidden(problem);
}

export function toastApiError(error: unknown, fallback: string): void {
  toast.error(apiErrorMessage(error, fallback));
}
